import { useCallback } from 'react'
import { setAccountId } from '../../../../api/account'
import {
  fetchAliveApps,
  fetchCaptureRules,
  getCodeOptions,
  fetchDelegations,
  fetchServices,
  fetchSetting,
  fetchUsers,
} from '../../../../api/commonApi'
import { LOCAL_AUTH_STORAGE_KEYS } from '../../auth/constants'
import {
  getStoredAccountId,
  setStoredAccountId,
} from '../../auth/utils/storage'
import { useConnectedAppsStore } from '../../../stores/useConnectedAppsStore'
import { useTimeEntriesStore } from '../../../stores/useTimeEntriesStore'
import { useUserStore } from '../../../stores/useUserStore'
import { useWorkspaceBootstrapStore } from '../../../stores/useWorkspaceBootstrapStore'
import { useWebExclusionStore } from '../../../stores/useWebExclusionStore'
import { getSortedDelegations } from '../../users/utils/delegations'
import { syncAuditLoggingStatus } from '../../connectedApps/utils/auditLoggingMonitor'
import { buildAssetUrl } from '../../../utils/assetUrl'
import { configureUserTimeZone } from '../../../utils/timeZone'
import { user } from '@/features/users/types'
import { isAdminVisibleUser } from '@/features/users/utils/is-admin-visible-user'
import { canViewAllTimekeepers } from '@/features/users/utils/permissions'

type UseBootstrapOptions = {
  hasCheckedHeartbeatOnce: boolean
}

export const useBootstrap = ({
  hasCheckedHeartbeatOnce,
}: UseBootstrapOptions) => {
  const setWorkspaceInitializing = useWorkspaceBootstrapStore(
    state => state.setWorkspaceInitializing
  )
  const selectedUser = useUserStore(state => state.selectedUser)
  const {
    setDelegations,
    setSelfUser,
    setSelectedUser,
    setEmailExclusionList,
  } = useUserStore()
  const {
    setCodes,
    setMinMinutes,
    setMinDailyWorkingHours,
    setFeatureAiUsage,
  } = useTimeEntriesStore()
  const {
    setServices,
    setHasDestination,
    setImanageConnected,
    setAgentEnabled,
    setDesktopAgentStatus,
    setChromeExtensionStatus,
    setEdgeExtensionStatus,
    setShowHeartBeatDialog,
  } = useConnectedAppsStore()
  const setWebExclusionValues = useWebExclusionStore(state => state.setValues)

  const initWorkspace = useCallback(async () => {
    const storedAccountId = getStoredAccountId()
    if (storedAccountId) {
      setAccountId(storedAccountId)
    }

    const user = JSON.parse(
      localStorage.getItem(LOCAL_AUTH_STORAGE_KEYS.user) || 'null'
    )
    const userAccountId = user?.account?.id ? String(user.account.id) : ''
    if (!storedAccountId && userAccountId) {
      setStoredAccountId(userAccountId)
      setAccountId(userAccountId)
    }
    configureUserTimeZone(user)

    const buildSelfUser = (userData: user): user => ({
      ...userData,
      id: userData.id,
      name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
      avatar: buildAssetUrl(userData.avatar || ''),
      email: userData.email || '',
    })

    const selfUser = buildSelfUser(user)

    setSelfUser(selfUser)

    if (!selectedUser && user) {
      setSelectedUser(selfUser)
    }

    const [
      usersRes,
      delegationsRes,
      emailCaptureRules,
      webCaptureRules,
      codeOptions,
    ] = await Promise.all([
      fetchUsers(),
      fetchDelegations(),
      fetchCaptureRules(`?user_id=${user?.id}&target_type=email&action=block`),
      fetchCaptureRules(`?user_id=${user?.id}&target_type=url&action=block`),
      getCodeOptions().catch(error => {
        console.error('Failed to load code options during bootstrap:', error)
        return null
      }),
    ])

    ;['TASKCODE', 'ACTIVITYCODE'].forEach(type => {
      if (!codeOptions || !Array.isArray(codeOptions[type])) return

      codeOptions[type].forEach(
        (option: { code: string; name: string; origin_name?: string }) => {
          if (!option.origin_name) {
            option.origin_name = option.name.startsWith(`${option.code} `)
              ? option.name.substring(option.code.length + 1)
              : option.name
          }
          option.name = `${option.code} ${option.origin_name}`
        }
      )
    })
    if (codeOptions) setCodes(codeOptions)

    const emailList = (emailCaptureRules || []).filter(
      (item: any) => item.target_type === 'email'
    )
    setEmailExclusionList(emailList)
    setWebExclusionValues(
      (Array.isArray(webCaptureRules) ? webCaptureRules : [])
        .map((item: { value?: unknown }) =>
          typeof item.value === 'string' ? item.value : ''
        )
        .filter(Boolean)
    )

    const rawUsers = Array.isArray(usersRes) ? usersRes : []
    const rawDelegations = Array.isArray(delegationsRes) ? delegationsRes : []
    const latestRawSelfUser = rawUsers.find(
      rawUser => String(rawUser.id) === String(user?.id)
    )
    const latestUserData = latestRawSelfUser
      ? { ...user, ...latestRawSelfUser }
      : user

    if (latestRawSelfUser) {
      localStorage.setItem(
        LOCAL_AUTH_STORAGE_KEYS.user,
        JSON.stringify(latestUserData)
      )
    }

    const latestSelfUser = buildSelfUser(latestUserData)

    setSelfUser(latestSelfUser)

    if (!selectedUser && user) {
      setSelectedUser(latestSelfUser)
    }

    const userMap = new Map(
      rawUsers.map((rawUser: any) => [
        rawUser.id,
        {
          ...rawUser,
          email: rawUser.email || rawUser.username || '',
          avatar: buildAssetUrl(rawUser.avatar || ''),
          name:
            rawUser.first_name && rawUser.last_name
              ? `${rawUser.first_name} ${rawUser.last_name}`
              : rawUser.name || rawUser.username,
        },
      ])
    )

    const delegations = rawDelegations
      .filter(
        (delegation: any) =>
          delegation.is_active && +delegation.delegatee === +user.id
      )
      .map((delegation: any) => userMap.get(delegation.delegator) || null)
      .filter(Boolean)

    if (canViewAllTimekeepers(latestSelfUser)) {
      setDelegations(
        Array.from(userMap.values()).filter(
          user => user.is_tkpr === true && isAdminVisibleUser(user)
        )
      )
    } else {
      setDelegations(
        delegations && delegations.length
          ? getSortedDelegations(delegations)
          : []
      )
    }

    const settings = await fetchSetting('')
    sessionStorage.setItem('settings', JSON.stringify(settings))

    settings.forEach((setting: any) => {
      if (setting.name === 'MINIMUM_WORKING_UNIT') {
        setMinMinutes(setting.value ?? 0)
      }

      if (setting.name === 'MINIMUM_DAILY_WORKING_HOURS') {
        setMinDailyWorkingHours(setting.value ?? 0)
      }

      if (setting.name === 'FEATURE_AI_USAGE') {
        setFeatureAiUsage(setting.value)
      }
    })

    const servicesResponse = await fetchServices()
    const services = servicesResponse || []
    setServices(services)
    syncAuditLoggingStatus(services)

    const imanage = services.find(
      (service: any) =>
        service.type === 's' && service.key === 'imw' && service.is_active
    )
    setImanageConnected(!!imanage)

    const destination = services.find(
      (service: any) => service.type === 'd' && service.is_active
    )
    setHasDestination(!!destination)

    const agent = services.find((service: any) => service.key === 'agent')
    setAgentEnabled(agent ? agent.is_active : false)

    const aliveApps = await fetchAliveApps('')
    setDesktopAgentStatus(aliveApps?.desktop_agent)
    setChromeExtensionStatus(aliveApps?.chromiumapp)
    setEdgeExtensionStatus(aliveApps?.edgeapp)

    const hasIssue =
      aliveApps?.desktop_agent !== 'online' ||
      (aliveApps?.chromiumapp !== 'online' && aliveApps?.edgeapp !== 'online')

    if (!hasCheckedHeartbeatOnce && hasIssue) {
      setShowHeartBeatDialog(true)
    }

    // Time entries are route data, not Bootstrap data. The route-aware React
    // Query in useTimeEntries loads a week only after initialization finishes.
    setWorkspaceInitializing(false)
  }, [
    hasCheckedHeartbeatOnce,
    selectedUser,
    setAgentEnabled,
    setChromeExtensionStatus,
    setDelegations,
    setDesktopAgentStatus,
    setEdgeExtensionStatus,
    setEmailExclusionList,
    setFeatureAiUsage,
    setHasDestination,
    setImanageConnected,
    setMinDailyWorkingHours,
    setMinMinutes,
    setCodes,
    setSelectedUser,
    setSelfUser,
    setServices,
    setShowHeartBeatDialog,
    setWebExclusionValues,
    setWorkspaceInitializing,
  ])

  return {
    initWorkspace,
  }
}
